import { Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FinanceType } from '../../../services/dtos/finances.dto';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import {
    RelationRequestsByTownDto,
    RelationRequestsCountDto,
    RelationRequestsTableDto,
    RelationType,
} from '../../../services/dtos/relation.dto';
import { getRelationRequestByTown, getRelationRequestByYear } from '../../../services/relation.service';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import logo_move_in from '../../../assets/img/emmenage.png';
import logo_move_out from '../../../assets/img/demenagement.png';
import logo_water from '../../../assets/img/question_qualite_de_l_eau.png';
import RequestCard from '../../commun/request-card';
import NoData from '../../commun/no-data';
import StyledTable from '../../commun/table';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';

/**
 * Ui pour la page d'assemblée générale
 * @constructor
 */
export default function RequestRelationUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        type: FinanceType | RelationType;
        handlerSetType: (newFinance: FinanceType | RelationType | undefined, text: string | undefined) => void;
    }>
) {
    // Document du meeting à voir et télécharger
    const [requestsByYear, setRequestsByYear] = useState<RelationRequestsCountDto>();
    // Document du meeting à voir et télécharger
    const [requestsByTown, setRequestsByTown] = useState<ResultSearchDto<RelationRequestsTableDto>>();
    // Document du meeting à voir et télécharger
    const [totalItems, setTotalItems] = useState<number>(0);
    // Page affichée deuxieme tableau
    const [page, setPage] = useState(0);
    // items par page deuxieme tableau
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // loader du meeting à voir et télécharger
    const [loading, setLoading] = useState(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<RelationRequestsTableDto, keyof RelationRequestsTableDto>[] = [
        { id: 'city', label: 'Etablissement', sort: false, width: 20 },
        { id: 'moveOutCount', label: 'Je déménage', sort: false, width: 20 },
        { id: 'moveInCount', label: "J'emménage", sort: false, width: 20 },
        { id: 'waterQualityCount', label: "Question qualité de l'eau", sort: false, width: 20 },
    ];

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setItemsPerPage(Number.parseInt(event.target.value, 10));
        setPage(0);
    };
    /**
     * Tous les flux demandes à l'ouverture de la page
     */
    useEffect(() => {
        if (props.mandate) {
            setLoading(true);
            getRelationRequestByYear(props.mandate.id)
                .then(async result => {
                    if (result) {
                        // Conversion du contenu en pdf et ouverture de celui-ci
                        setRequestsByYear(result);
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [props.mandate, showMessage]);

    /**
     * Demandes par communes à l'ouverture de la page, puis à chauqe changement de page ou de itemsParPage
     */
    useEffect(() => {
        if (props.mandate) {
            setLoading(true);
            getRelationRequestByTown(props.mandate.id, page, itemsPerPage)
                .then(async result => {
                    if (result) {
                        // Traitement de la données pour pouvoir l'afficher dans le tableau
                        const requests: RelationRequestsTableDto[] = result.items.map(
                            (item: RelationRequestsByTownDto) => ({
                                city: item.city,
                                moveInCount: item.requestsCount.moveInCount.toString(),
                                moveOutCount: item.requestsCount.moveOutCount.toString(),
                                waterQualityCount: item.requestsCount.waterQualityCount.toString(),
                            })
                        );
                        setTotalItems(result.totalItems);
                        setRequestsByTown({
                            ...result,
                            items: requests,
                        });
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des documents');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [page, itemsPerPage, props.mandate, showMessage]);

    //Mise en place du tableau reunion passées
    const showTableRequests = () => {
        if (requestsByTown === undefined) {
            // Affichage du skeleton le temps de recevoir les données
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (totalItems === 0) {
            // Si pas de données on envoit no data
            return <NoData title={'Information : '} content={'Aucune information à venir'} />;
        } else {
            return (
                // Table stylisée
                <StyledTable
                    data={requestsByTown.items}
                    searching={loading}
                    headCells={headCells}
                    totalItems={requestsByTown.totalItems}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={itemsPerPage}
                ></StyledTable>
            );
        }
    };

    //Retourne la vue
    return (
        <Grid container className={'relation-request-content'} spacing={2}>
            <Grid item xs={12}>
                {/* Affichage de tous les flux demandes remontés par Crusoé */}
                {requestsByYear && (
                    <>
                        <Grid container spacing={2}>
                            {requestsByYear?.moveOutCount !== undefined && (
                                <Grid item md={4} xs={12}>
                                    <RequestCard
                                        data={requestsByYear.moveOutCount}
                                        text={'Je déménage'}
                                        img={logo_move_out}
                                    />
                                </Grid>
                            )}
                            {requestsByYear?.moveInCount !== undefined && (
                                <Grid item md={4} xs={12}>
                                    <RequestCard
                                        data={requestsByYear.moveInCount}
                                        text={"J'emménage"}
                                        img={logo_move_in}
                                    />
                                </Grid>
                            )}
                            {requestsByYear?.waterQualityCount !== undefined && (
                                <Grid item md={4} xs={12}>
                                    <RequestCard
                                        data={requestsByYear.waterQualityCount}
                                        text={"Question qualité de l'eau"}
                                        img={logo_water}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </>
                )}
            </Grid>
            {/* Affichage du tableau des communes */}
            <Grid item xs={12}>
                <Typography className="relationUi__sectionTitle" variant={'h5'}>
                    Nombre de demandes par communes
                </Typography>
            </Grid>
            <Grid item xs={12}>
                {/* Tableau des communes */}
                {showTableRequests()}
            </Grid>
        </Grid>
    );
}
